import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  where,
  doc,
  updateDoc,
  collection,
  query,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  DoorClosed,
  UserPlus,
  Video,
  X,
  XCircle,
} from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { Button, InfoTooltip } from '../components/ui';
import { marcarAceptacionPropia } from '../services/aceptacionPropia';
import { Modal } from '../components/ui/Modal';
import {
  ControlEstadoMedico,
  IndicadoresSala,
  type EstadoMedico,
} from '../components/sala';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useCollection, useDocument } from '../hooks/useFirestore';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { db } from '../config/firebase';
import type { Consulta, Sala } from '../types';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { EmptyState } from '../components/ui/EmptyState';
import {
  aceptarConsulta as aceptarConsultaSvc,
  cancelarConsultasSupervisor,
  medicoEntrarSala as medicoEntrarSalaSvc,
  medicoSalirSala as medicoSalirSalaSvc,
  iniciarBreakAlmuerzo as iniciarBreakAlmuerzoSvc,
  terminarBreakAlmuerzo as terminarBreakAlmuerzoSvc,
  devolverConsultaACola as devolverConsultaAColaSvc,
} from '../services/consultaService';
import { fixMojibake } from '../utils/fixMojibake';
import styles from './SalasPage.module.css';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

interface ConsultaWithDate extends Omit<Consulta, 'creadoEn'> {
  creadoEn: Date;
}

type SortField = 'creadoEn' | 'pacienteNombre' | 'motivoConsulta' | 'salaNombre';
type SortDirection = 'asc' | 'desc';

/** Ancho a partir del cual la cola cabe como tabla sin recortar columnas. */
const ANCHO_TABLA = '(min-width: 900px)';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function formatWaitTime(createdAt: Date): string {
  const diffMs = Date.now() - createdAt.getTime();
  const totalMinutes = Math.floor(diffMs / 60000);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours} h ${minutes} min`;
}

/**
 * Severidad de la espera. Tres niveles y no dos: un paciente de 20 minutos ya
 * merece atención antes que uno de 3, y con un único umbral en 30 toda la cola
 * se veía igual hasta que de golpe se ponía roja.
 */
function nivelEspera(createdAt: Date): 'normal' | 'aviso' | 'urgente' {
  const min = (Date.now() - createdAt.getTime()) / 60000;
  if (min >= 30) return 'urgente';
  if (min >= 15) return 'aviso';
  return 'normal';
}

function formatCurrentTime(): string {
  return new Date().toLocaleTimeString('es-PA', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Constraints para la query de consultas en espera del médico.
 * Para médicos con salaIds específicos filtramos explícitamente para cumplir
 * con las Firestore rules. Admin/supervisor con '*' acceden sin filtro.
 */
function buildEnEsperaConstraints(userSalaIds: string[] | undefined, role: string | null) {
  const base = [where('estado', '==', 'en_espera')];
  if (!userSalaIds || userSalaIds.length === 0) {
    // Un MÉDICO sin salas asignadas no debe ver a NINGÚN paciente (antes esta
    // rama devolvía la query sin filtro → veía la cola de todas las salas).
    // Admin/supervisor sí ven todo (las rules ya se lo permiten por rol).
    return role === 'medico' ? [...base, where('salaId', '==', '__sin_salas__')] : base;
  }
  if (userSalaIds.includes('*')) return base;
  return [...base, where('salaId', 'in', userSalaIds.slice(0, 30))];
}

// ---------------------------------------------------------------------------
// SalasPage — puesto de trabajo del médico
// ---------------------------------------------------------------------------

export default function SalasPage() {
  const navigate = useNavigate();
  const { user, role, claims } = useAuth();
  const puedeAtender = role === 'medico';
  // Cancelación de pacientes en espera: exclusiva del Supervisor (no admin).
  const puedeCancelar = role === 'supervisor';
  const { addToast } = useToast();
  const cabeTabla = useMediaQuery(ANCHO_TABLA);

  // ── Salas data ───────────────────────────────────────────
  const { data: salasData } = useCollection<Sala>('salas');

  const salaMap = useMemo(() => {
    const map: Record<string, Sala> = {};
    if (salasData) {
      for (const s of salasData) map[s.id] = s;
    }
    return map;
  }, [salasData]);

  // ── Consultas en espera (todas las salas asignadas) ──────
  const enEsperaConstraints = useMemo(
    () => buildEnEsperaConstraints(claims?.salaIds, role),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [claims?.salaIds?.join(','), role],
  );
  const { data: firestoreConsultas } = useCollection<Consulta>('consultas', enEsperaConstraints);

  // ── Membresía REAL por sala (issue #11): un médico solo ve pacientes de
  //    salas donde está asignado Y habilitado. Los claims pueden quedar
  //    desincronizados (se refrescan con retraso al quitarlo de una sala) y no
  //    conocen `medicos_sala.habilitado` (lo escribe el supervisor al
  //    deshabilitarlo) — se verifica contra la subcolección, que es la fuente
  //    de verdad. null = sin filtro extra (admin/supervisor o comodín '*').
  const [salasHabilitadas, setSalasHabilitadas] = useState<Set<string> | null>(null);
  useEffect(() => {
    const salaIds = claims?.salaIds ?? [];
    if (role !== 'medico' || salaIds.includes('*')) { setSalasHabilitadas(null); return; }
    if (!user?.uid || salaIds.length === 0) { setSalasHabilitadas(new Set()); return; }
    let cancelado = false;
    const uid = user.uid;
    Promise.all(salaIds.map(async (salaId) => {
      try {
        const snap = await getDoc(doc(db, 'salas', salaId, 'medicos_sala', uid));
        return snap.exists() && snap.data()?.habilitado !== false ? salaId : null;
      } catch { return null; }
    })).then((ids) => {
      if (!cancelado) setSalasHabilitadas(new Set(ids.filter((x): x is string => x !== null)));
    });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, user?.uid, claims?.salaIds?.join(',')]);

  const consultasVisibles = useMemo(() => {
    if (!firestoreConsultas || !salasHabilitadas) return firestoreConsultas;
    return firestoreConsultas.filter((c) => salasHabilitadas.has(c.salaId));
  }, [firestoreConsultas, salasHabilitadas]);

  // ── Ofertas híbridas sin responder (solo supervisor) ─────
  const esSupervisor = role === 'supervisor';
  const ofertasConstraints = useMemo(
    () => (esSupervisor ? [where('estado', '==', 'asignada')] : undefined),
    [esSupervisor],
  );
  const { data: firestoreOfertas } = useCollection<Consulta>('consultas', ofertasConstraints);
  const [tabActiva, setTabActiva] = useState<'cola' | 'ofertas'>('cola');
  const [devolviendoId, setDevolviendoId] = useState<string | null>(null);

  const handleDevolverACola = useCallback(
    async (consulta: Consulta) => {
      setDevolviendoId(consulta.id);
      try {
        await devolverConsultaAColaSvc(consulta.id);
        addToast('success', `${consulta.pacienteNombre} devuelto a la cola`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'No se pudo devolver a la cola';
        addToast('error', msg);
      } finally {
        setDevolviendoId(null);
      }
    },
    [addToast],
  );

  // ── Bloqueo si hay consulta activa sin finalizar ─────────
  const { data: medicoDoc } = useDocument<{
    consultaActiva?: string | null;
    autoAsignacionEnabled?: boolean;
  }>('medicos', user?.uid);
  const consultaActivaId = medicoDoc?.consultaActiva ?? null;
  const tieneConsultaActiva = !!consultaActivaId;

  // ── UI state ─────────────────────────────────────────────
  const [cedulaMap, setCedulaMap] = useState<Record<string, string>>({});
  const [attendingId, setAttendingId] = useState<string | null>(null);
  const [motivoModal, setMotivoModal] = useState<{ nombre: string; motivo: string } | null>(null);
  const [sortField, setSortField] = useState<SortField>('creadoEn');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentTime, setCurrentTime] = useState(formatCurrentTime());
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [counterBounce, setCounterBounce] = useState(false);
  const [estadoMedico, setEstadoMedico] = useState<EstadoMedico>('disponible');
  const [autoAsignacion, setAutoAsignacion] = useState(true);
  const [autoAsignacionLoading, setAutoAsignacionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [cancelando, setCancelando] = useState(false);
  const [salaFiltro, setSalaFiltro] = useState<string>('todas');

  // Sincronizar con el valor persistido en Firestore (medicos/{uid}.autoAsignacionEnabled).
  useEffect(() => {
    if (medicoDoc?.autoAsignacionEnabled !== undefined) {
      setAutoAsignacion(medicoDoc.autoAsignacionEnabled);
    }
  }, [medicoDoc?.autoAsignacionEnabled]);

  // Mientras está en pausa, mostrar el toggle como "Manual" sin tocar el
  // valor persistido — al reactivarse vuelve a reflejar la preferencia real.
  const autoAsignacionDisplay = estadoMedico === 'pausa' ? false : autoAsignacion;

  // ── Notificaciones de nuevos pacientes ───────────────────
  const playNotification = useNotificationSound();
  const prevIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (!consultasVisibles) return;
    const currentIds = new Set(consultasVisibles.map((c) => c.id));

    if (prevIdsRef.current !== null) {
      const arrivedIds: string[] = [];
      const leftIds: string[] = [];
      for (const id of currentIds) {
        if (!prevIdsRef.current.has(id)) arrivedIds.push(id);
      }
      for (const id of prevIdsRef.current) {
        if (!currentIds.has(id)) leftIds.push(id);
      }

      if (leftIds.length > 0) {
        setRemovingIds((prev) => {
          const next = new Set(prev);
          leftIds.forEach((id) => next.add(id));
          return next;
        });
        setTimeout(() => {
          setRemovingIds((prev) => {
            const next = new Set(prev);
            leftIds.forEach((id) => next.delete(id));
            return next;
          });
        }, 450);
      }

      if (arrivedIds.length > 0) {
        playNotification();

        setNewIds((prev) => {
          const next = new Set(prev);
          arrivedIds.forEach((id) => next.add(id));
          return next;
        });
        const timer = setTimeout(() => {
          setNewIds((prev) => {
            const next = new Set(prev);
            arrivedIds.forEach((id) => next.delete(id));
            return next;
          });
        }, 8000);

        setCounterBounce(true);
        setTimeout(() => setCounterBounce(false), 600);

        const newConsulta = consultasVisibles.find((c) => c.id === arrivedIds[0]);
        const patientName = newConsulta?.pacienteNombre || 'Paciente';
        const salaNombre = newConsulta?.salaNombre || '';

        if (arrivedIds.length === 1) {
          addToast('info', `${patientName} solicita atención${salaNombre ? ` · ${salaNombre}` : ''}`);
        } else {
          addToast('info', `${arrivedIds.length} nuevos pacientes en cola`);
        }

        if (
          'Notification' in window &&
          Notification.permission === 'granted' &&
          document.hidden
        ) {
          new Notification('Nueva solicitud de consulta', {
            body: `${patientName}${salaNombre ? ` · ${salaNombre}` : ''} (${consultasVisibles.length} en espera)`,
            icon: '/minsalud-icon.png',
            tag: 'new-patient-salas',
          });
        }

        return () => clearTimeout(timer);
      }
    }
    prevIdsRef.current = currentIds;
  }, [consultasVisibles, playNotification, addToast]);

  // ── Procesamiento de consultas ───────────────────────────
  const allConsultas: ConsultaWithDate[] = useMemo(() => {
    if (!consultasVisibles) return [];
    return consultasVisibles.map((c) => ({
      ...c,
      creadoEn:
        c.creadoEn && typeof (c.creadoEn as { toDate?: () => Date }).toDate === 'function'
          ? (c.creadoEn as { toDate: () => Date }).toDate()
          : new Date(),
    }));
  }, [consultasVisibles]);

  // ── Salas del usuario ────────────────────────────────────
  // Para el médico son las que tiene habilitadas; para supervisión, las salas
  // activas. Es la lista que gobierna el selector y los indicadores.
  const salasDelUsuario = useMemo(() => {
    const todas = (salasData ?? []).filter((s) => s.activa !== false);
    if (role !== 'medico' || salasHabilitadas === null) return todas;
    return todas.filter((s) => salasHabilitadas.has(s.id));
  }, [salasData, role, salasHabilitadas]);

  const conteoPorSala = useMemo(() => {
    const conteo: Record<string, number> = {};
    for (const c of allConsultas) conteo[c.salaId] = (conteo[c.salaId] ?? 0) + 1;
    return conteo;
  }, [allConsultas]);

  const salasOrdenadas = useMemo(
    () => [...salasDelUsuario].sort((a, b) => {
      const dif = (conteoPorSala[b.id] ?? 0) - (conteoPorSala[a.id] ?? 0);
      return dif !== 0 ? dif : a.nombre.localeCompare(b.nombre);
    }),
    [salasDelUsuario, conteoPorSala],
  );

  // El caso normal es un médico con UNA sala: entonces no hay nada que elegir
  // y el selector desaparece; queda solo el estado de esa sala.
  const salaUnica = salasDelUsuario.length === 1 ? salasDelUsuario[0] : null;
  const variasSalas = salasDelUsuario.length > 1;

  // Si el filtro apuntaba a una sala que dejó de estar disponible, volver a
  // "todas" en lugar de mostrar una cola vacía sin explicación.
  useEffect(() => {
    if (salaFiltro !== 'todas' && !salasDelUsuario.some((s) => s.id === salaFiltro)) {
      setSalaFiltro('todas');
    }
  }, [salaFiltro, salasDelUsuario]);

  const salaEnFoco = salaUnica ?? (salaFiltro !== 'todas' ? salaMap[salaFiltro] : undefined);
  const mostrarColumnaSala = !salaUnica && salaFiltro === 'todas';

  const consultasFiltradas = useMemo(
    () => (salaFiltro === 'todas'
      ? allConsultas
      : allConsultas.filter((c) => c.salaId === salaFiltro)),
    [allConsultas, salaFiltro],
  );

  // Ofertas híbridas — "desde cuándo" es asignadaEn (cuándo se le ofreció al
  // médico), no creadoEn (cuándo el paciente entró a la cola originalmente).
  const ofertasPendientes = useMemo(() => {
    if (!firestoreOfertas) return [];
    return [...firestoreOfertas]
      .map((c) => {
        const asignada = c.asignadaEn as unknown as { toDate?: () => Date } | undefined;
        return {
          ...c,
          asignadaEnDate: asignada && typeof asignada.toDate === 'function' ? asignada.toDate() : new Date(),
        };
      })
      .sort((a, b) => a.asignadaEnDate.getTime() - b.asignadaEnDate.getTime());
  }, [firestoreOfertas]);

  const sortedConsultas = useMemo(() => {
    return [...consultasFiltradas].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'creadoEn':
          cmp = a.creadoEn.getTime() - b.creadoEn.getTime();
          break;
        case 'pacienteNombre':
          cmp = a.pacienteNombre.localeCompare(b.pacienteNombre);
          break;
        case 'motivoConsulta':
          cmp = a.motivoConsulta.localeCompare(b.motivoConsulta);
          break;
        case 'salaNombre':
          cmp = (a.salaNombre || '').localeCompare(b.salaNombre || '');
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [consultasFiltradas, sortField, sortDirection]);

  // ── Cédulas: batch-fetch desde colección pacientes ───────
  const pacienteUidsKey = allConsultas.map((c) => c.pacienteId).join(',');
  useEffect(() => {
    if (!allConsultas.length) return;
    const uids = [...new Set(allConsultas.map((c) => c.pacienteId).filter(Boolean))];
    if (!uids.length) return;
    const batches: string[][] = [];
    for (let i = 0; i < uids.length; i += 30) batches.push(uids.slice(i, i + 30));
    Promise.all(
      batches.map((batch) =>
        getDocs(query(collection(db, 'pacientes'), where('__name__', 'in', batch))),
      ),
    ).then((snapshots) => {
      const map: Record<string, string> = {};
      for (const snap of snapshots) {
        for (const d of snap.docs) {
          const cedula = (d.data() as { cedula?: string }).cedula;
          if (cedula) map[d.id] = cedula;
        }
      }
      setCedulaMap(map);
    }).catch(() => { /* sin acceso → sin cédula */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pacienteUidsKey]);

  // ── Timers ───────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(formatCurrentTime()), 60000);
    return () => clearInterval(interval);
  }, []);

  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // ── Orden ────────────────────────────────────────────────
  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    },
    [sortField],
  );

  const encabezadoOrden = (field: SortField, etiqueta: string) => {
    const activa = sortField === field;
    return (
      <button
        type="button"
        className={`${styles.thBtn} ${activa ? styles.thBtnActiva : ''}`}
        onClick={() => handleSort(field)}
        title={`Ordenar por ${etiqueta}`}
      >
        {etiqueta}
        <span className={styles.thFlecha} aria-hidden="true">
          {!activa ? <ArrowUpDown size={12} />
            : sortDirection === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </span>
      </button>
    );
  };

  // ── Estado del médico ────────────────────────────────────
  // "Disponible"/"Pausar" tiene efecto real en el backend: agrega/quita al
  // médico de `medicosActivos` en CADA sala a la que está asignado — es lo
  // que el cron de capacidad dinámica (reconciliarCuposEnVivo) usa para saber
  // cuántos médicos hay en turno.
  const [estadoMedicoLoading, setEstadoMedicoLoading] = useState(false);
  const handleChangeEstadoMedico = useCallback(
    async (next: EstadoMedico) => {
      const salasDestino = (claims?.salaIds ?? []).filter((id) => id !== '*');
      setEstadoMedicoLoading(true);
      try {
        const accion = next === 'disponible' ? medicoEntrarSalaSvc : medicoSalirSalaSvc;
        const resultados = await Promise.allSettled(salasDestino.map((salaId) => accion(salaId)));
        // Una sala cerrada/inactiva rechaza con 'failed-precondition' — no es
        // un error real, es esperado que un médico no pueda "activarse" en una
        // sala fuera de horario.
        const exitosos = resultados.filter((r) => r.status === 'fulfilled').length;
        const rechazos = resultados.filter(
          (r): r is PromiseRejectedResult =>
            r.status === 'rejected' && (r.reason as { code?: string })?.code !== 'functions/failed-precondition',
        );
        const fallos = rechazos.length;
        // El motivo real —"No está asignado a esta sala", "Sala inactiva"— venía
        // en el rechazo y se descartaba, dejando al médico con un aviso que no
        // decía nada ni se podía abrir (issue #43). Se muestra tal cual.
        const motivo = (rechazos[0]?.reason as { message?: string } | undefined)?.message?.trim();
        setEstadoMedico(next);
        if (fallos > 0 && fallos < resultados.length) {
          addToast(
            'error',
            `No se pudo actualizar el estado en ${fallos} de sus salas${motivo ? `: ${motivo}` : ''}`,
          );
        } else if (fallos > 0 && resultados.length > 0) {
          addToast(
            'error',
            motivo ? `No se pudo actualizar el estado: ${motivo}` : 'No se pudo actualizar el estado',
          );
        } else if (next === 'disponible' && exitosos === 0 && resultados.length > 0) {
          addToast('info', 'Todas sus salas están cerradas ahora mismo — no recibirá pacientes hasta que abran');
        } else {
          addToast(
            next === 'disponible' ? 'success' : 'info',
            next === 'disponible'
              ? 'Estás disponible para nuevos pacientes'
              : 'Estás en pausa — no recibirás asignaciones',
          );
        }
      } finally {
        setEstadoMedicoLoading(false);
      }
    },
    [addToast, claims?.salaIds],
  );

  const handleToggleAutoAsignacion = useCallback(
    async (next: boolean) => {
      if (!user?.uid) return;
      const previous = autoAsignacion;
      setAutoAsignacion(next); // optimista
      setAutoAsignacionLoading(true);
      try {
        await updateDoc(doc(db, 'medicos', user.uid), { autoAsignacionEnabled: next });
        addToast(
          'info',
          next
            ? 'Auto-asignación activada: recibirá pacientes automáticamente'
            : 'Auto-asignación desactivada: deberá tomar pacientes manualmente de la cola',
        );
      } catch (error) {
        console.error('[SalasPage] Error actualizando auto-asignación:', error);
        setAutoAsignacion(previous); // revertir
        addToast('error', 'No se pudo guardar la preferencia de auto-asignación');
      } finally {
        setAutoAsignacionLoading(false);
      }
    },
    [addToast, autoAsignacion, user?.uid],
  );

  const handleIniciarBreakAlmuerzo = useCallback(async () => {
    setEstadoMedicoLoading(true);
    try {
      await iniciarBreakAlmuerzoSvc();
      setEstadoMedico('break_almuerzo');
      addToast('info', 'Iniciaste tu break de almuerzo — no recibirás pacientes nuevos');
    } catch (error) {
      console.error('[SalasPage] Error iniciando break de almuerzo:', error);
      addToast('error', 'No se pudo iniciar el break de almuerzo');
    } finally {
      setEstadoMedicoLoading(false);
    }
  }, [addToast]);

  const handleTerminarBreakAlmuerzo = useCallback(async () => {
    setEstadoMedicoLoading(true);
    try {
      await terminarBreakAlmuerzoSvc();
      setEstadoMedico('disponible');
      addToast('success', 'Volviste de tu break — ya puedes recibir pacientes');
    } catch (error) {
      console.error('[SalasPage] Error terminando break de almuerzo:', error);
      addToast('error', 'No se pudo terminar el break de almuerzo');
    } finally {
      setEstadoMedicoLoading(false);
    }
  }, [addToast]);

  // ── Atender consulta ─────────────────────────────────────
  const handleAtenderAI = useCallback(
    async (consulta: ConsultaWithDate) => {
      if (tieneConsultaActiva) {
        addToast(
          'error',
          'Tiene una consulta sin finalizar. Complete la consulta activa antes de tomar otra.',
        );
        return;
      }
      setAttendingId(consulta.id);
      // Marca "acción propia" ANTES de la llamada: escribe consultaActiva en
      // el mismo write, y el banner global de asignación no debe confundir
      // esto con una asignación sorpresa y sonar la alarma.
      marcarAceptacionPropia(consulta.id);
      try {
        await aceptarConsultaSvc(consulta.id);
        addToast('success', `Consulta AI iniciada con ${consulta.pacienteNombre}`);
        setAttendingId(null);
        navigate(`/consulta/${consulta.id}/video-ai`);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Error al iniciar consulta';
        addToast('error', msg);
        setAttendingId(null);
      }
    },
    [addToast, navigate, tieneConsultaActiva],
  );

  // ─── Cancelación por supervisor ─────────────────────────
  const toggleSelected = useCallback((consultaId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(consultaId)) next.delete(consultaId);
      else next.add(consultaId);
      return next;
    });
  }, []);

  const allSelected = sortedConsultas.length > 0 && selectedIds.size === sortedConsultas.length;

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === sortedConsultas.length
        ? new Set()
        : new Set(sortedConsultas.map((c) => c.id)),
    );
  }, [sortedConsultas]);

  const handleConfirmCancelacion = useCallback(async () => {
    const motivo = motivoCancelacion.trim();
    if (!motivo) {
      addToast('error', 'Ingrese el motivo de la cancelación');
      return;
    }
    const consultaIds = Array.from(selectedIds);
    if (consultaIds.length === 0) return;

    setCancelando(true);
    try {
      const { resultados } = await cancelarConsultasSupervisor(consultaIds, motivo);
      const exitosos = resultados.filter((r) => r.success).length;
      const fallidos = resultados.length - exitosos;

      if (exitosos > 0) {
        addToast(
          'success',
          exitosos === 1
            ? 'Se canceló la consulta y se notificó al paciente'
            : `Se cancelaron ${exitosos} consultas y se notificó a los pacientes`,
        );
      }
      if (fallidos > 0) {
        addToast(
          'error',
          exitosos > 0
            ? `${fallidos} no se pudieron cancelar (ya no estaban en espera)`
            : 'No se pudo cancelar la selección — ya no estaban en espera',
        );
      }

      setSelectedIds(new Set());
      setMotivoCancelacion('');
      setShowCancelModal(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo procesar la cancelación';
      addToast('error', msg);
    } finally {
      setCancelando(false);
    }
  }, [addToast, motivoCancelacion, selectedIds]);

  const totalEnEspera = allConsultas.length;
  const enFoco = consultasFiltradas.length;
  const medicoSinSalas = puedeAtender && salasHabilitadas !== null && salasHabilitadas.size === 0;
  const viendoCola = !esSupervisor || tabActiva === 'cola';

  // ---------------------------------------------------------------------------
  // Piezas de la cola
  // ---------------------------------------------------------------------------

  /** Datos de una fila que se usan igual en la tabla y en la tarjeta. */
  const datosFila = (consulta: ConsultaWithDate) => ({
    nombre: fixMojibake(consulta.pacienteNombre),
    sala: consulta.salaNombre || salaMap[consulta.salaId]?.nombre || consulta.salaId,
    motivo: consulta.motivoConsulta || '',
    cedula: cedulaMap[consulta.pacienteId],
    nivel: nivelEspera(consulta.creadoEn),
    esNuevo: newIds.has(consulta.id),
    saliendo: removingIds.has(consulta.id),
    tomando: attendingId === consulta.id,
  });

  const botonTomar = (consulta: ConsultaWithDate, ancho: boolean) => {
    const tomando = attendingId === consulta.id;
    return (
      <button
        type="button"
        className={`${styles.btnTomar} ${ancho ? styles.btnTomarAncho : ''}`}
        disabled={!puedeAtender || attendingId !== null || tieneConsultaActiva}
        title={
          !puedeAtender ? 'Solo los médicos pueden tomar consultas'
            : tieneConsultaActiva ? 'Finalice su consulta activa antes de tomar otra'
              : undefined
        }
        onClick={() => handleAtenderAI(consulta)}
      >
        <Video size={15} aria-hidden="true" />
        {tomando ? 'Iniciando…' : 'Tomar consulta'}
      </button>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <MainLayout>
      <div className={styles.pagina}>

        {/* ── Turno del médico ─────────────────────────────── */}
        {puedeAtender && (
          <ControlEstadoMedico
            estado={estadoMedico}
            autoAsignacionEnabled={autoAsignacionDisplay}
            onChangeEstado={handleChangeEstadoMedico}
            onToggleAutoAsignacion={handleToggleAutoAsignacion}
            onIniciarBreakAlmuerzo={handleIniciarBreakAlmuerzo}
            onTerminarBreakAlmuerzo={handleTerminarBreakAlmuerzo}
            loading={estadoMedicoLoading || autoAsignacionLoading}
          />
        )}

        {/* Consulta sin finalizar: el motivo real por el que los botones de la
            cola están apagados. Antes solo se veían deshabilitados. */}
        {tieneConsultaActiva && consultaActivaId && (
          <div className={styles.avisoActiva} role="status">
            <div className={styles.avisoActivaTexto}>
              <strong>Tiene una consulta sin finalizar.</strong>
              <span>Complétela para poder tomar al siguiente paciente de la cola.</span>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(`/consulta/${consultaActivaId}/video-ai`)}
            >
              Volver a la consulta
            </Button>
          </div>
        )}

        {/* ── Encabezado: cuántos esperan y dónde ──────────── */}
        <header className={styles.encabezado}>
          <div className={styles.encabezadoInfo}>
            <h1 className={styles.titulo}>
              {salaUnica ? salaUnica.nombre : 'Pacientes en espera'}
              {puedeAtender && (
                <InfoTooltip text='Cola compartida: son los pacientes esperando teleconsulta en las salas donde usted atiende. No están asignados solo a usted — cualquier médico de esa sala puede tomar cualquiera con "Tomar consulta".' />
              )}
            </h1>
            <p className={styles.subtitulo}>
              <span className={styles.puntoVivo} aria-hidden="true" />
              En vivo · {currentTime}
              {salaUnica?.descripcion ? ` · ${salaUnica.descripcion}` : ''}
            </p>
          </div>
          <div className={styles.contador}>
            <span className={`${styles.contadorNum} ${counterBounce ? styles.contadorBote : ''}`}>
              {enFoco}
            </span>
            <span className={styles.contadorEtiqueta}>
              {enFoco === 1 ? 'en espera' : 'en espera'}
              {salaFiltro !== 'todas' && totalEnEspera !== enFoco && (
                <em className={styles.contadorTotal}>de {totalEnEspera} en total</em>
              )}
            </span>
          </div>
        </header>

        {/* ── Selector de sala (solo si hay más de una) ────── */}
        {variasSalas && (
          <div className={styles.selectorSalas} role="tablist" aria-label="Filtrar por sala">
            <button
              type="button"
              role="tab"
              aria-selected={salaFiltro === 'todas'}
              className={`${styles.chipSala} ${salaFiltro === 'todas' ? styles.chipSalaActivo : ''}`}
              onClick={() => setSalaFiltro('todas')}
            >
              Todas mis salas
              <span className={styles.chipNum}>{totalEnEspera}</span>
            </button>
            {salasOrdenadas.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={salaFiltro === s.id}
                className={`${styles.chipSala} ${salaFiltro === s.id ? styles.chipSalaActivo : ''}`}
                onClick={() => setSalaFiltro(s.id)}
              >
                {s.nombre}
                <span className={styles.chipNum}>{conteoPorSala[s.id] ?? 0}</span>
              </button>
            ))}
          </div>
        )}

        {/* ── Estado operativo de la sala en foco ──────────── */}
        {salaEnFoco && (
          <IndicadoresSala sala={salaEnFoco} pacientesEnEspera={conteoPorSala[salaEnFoco.id] ?? 0} />
        )}

        {/* ── Tabs supervisor: Cola / Ofertas ──────────────── */}
        {esSupervisor && (
          <nav className={styles.tabsRow} role="tablist" aria-label="Vista de cola">
            <button
              type="button"
              role="tab"
              aria-selected={tabActiva === 'cola'}
              className={`${styles.tabBtn} ${tabActiva === 'cola' ? styles.tabBtnActive : ''}`}
              onClick={() => setTabActiva('cola')}
            >
              Cola
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tabActiva === 'ofertas'}
              className={`${styles.tabBtn} ${tabActiva === 'ofertas' ? styles.tabBtnActive : ''}`}
              onClick={() => setTabActiva('ofertas')}
            >
              Ofertas pendientes
              {ofertasPendientes.length > 0 && (
                <span className={styles.tabBadge}>{ofertasPendientes.length}</span>
              )}
            </button>
          </nav>
        )}

        {/* ── Barra de acción masiva ───────────────────────── */}
        {viendoCola && puedeCancelar && selectedIds.size > 0 && (
          <div className={styles.barraMasiva}>
            <span className={styles.barraMasivaTexto}>
              {selectedIds.size} paciente{selectedIds.size === 1 ? '' : 's'} seleccionado{selectedIds.size === 1 ? '' : 's'}
            </span>
            <div className={styles.barraMasivaBotones}>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                Deseleccionar
              </Button>
              <Button variant="danger" size="sm" onClick={() => setShowCancelModal(true)}>
                <XCircle size={16} />
                Cancelar seleccionados
              </Button>
            </div>
          </div>
        )}

        {/* ── Cola ─────────────────────────────────────────── */}
        {viendoCola && (
          medicoSinSalas ? (
            <EmptyState
              variant="queue"
              title="Aún no tiene salas asignadas"
              message="No verá pacientes hasta que el supervisor lo asigne a una sala de atención. Si cree que ya debería estarlo, comuníquese con supervisión."
            />
          ) : sortedConsultas.length === 0 ? (
            <EmptyState
              variant="queue"
              title={salaFiltro === 'todas' ? 'No hay pacientes en espera' : 'Esta sala no tiene pacientes en espera'}
              message={
                salaFiltro === 'todas'
                  ? 'Los pacientes aparecerán aquí en cuanto soliciten una teleconsulta desde la aplicación móvil.'
                  : 'Cambie a "Todas mis salas" para ver la cola completa.'
              }
            />
          ) : cabeTabla ? (
            /* ── Tabla (pantallas anchas) ── */
            <div className={styles.tarjetaTabla}>
              <table className={styles.tabla}>
                {/* El motivo es la única columna sin ancho fijo: absorbe la
                    holgura y se recorta con ellipsis. Las demás llevan medida
                    propia porque `table-layout: fixed` reparte a partes
                    iguales lo que no se declara, y el nombre del paciente
                    quedaba cortado a la mitad. */}
                <colgroup>
                  {puedeCancelar && <col style={{ width: 44 }} />}
                  <col style={{ width: '26%' }} />
                  {mostrarColumnaSala && <col style={{ width: 160 }} />}
                  <col />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 172 }} />
                </colgroup>
                <thead>
                  <tr>
                    {puedeCancelar && (
                      <th>
                        <input
                          type="checkbox"
                          className={styles.check}
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          aria-label="Seleccionar todos los pacientes en espera"
                        />
                      </th>
                    )}
                    <th>{encabezadoOrden('pacienteNombre', 'Paciente')}</th>
                    {mostrarColumnaSala && <th>{encabezadoOrden('salaNombre', 'Sala')}</th>}
                    <th>{encabezadoOrden('motivoConsulta', 'Motivo de consulta')}</th>
                    <th>{encabezadoOrden('creadoEn', 'Espera')}</th>
                    <th className={styles.thAccion}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedConsultas.map((consulta, index) => {
                    const d = datosFila(consulta);
                    const clases = [
                      index === 0 ? styles.filaPrimera : '',
                      d.esNuevo ? styles.filaNueva : '',
                      d.saliendo ? styles.filaSaliendo : styles.filaEntrando,
                    ].filter(Boolean).join(' ');

                    return (
                      <tr key={consulta.id} className={clases}>
                        {puedeCancelar && (
                          <td>
                            <input
                              type="checkbox"
                              className={styles.check}
                              checked={selectedIds.has(consulta.id)}
                              onChange={() => toggleSelected(consulta.id)}
                              aria-label={`Seleccionar a ${d.nombre}`}
                            />
                          </td>
                        )}

                        <td>
                          <div className={styles.celdaPaciente}>
                            <div className={`${styles.avatar} ${d.esNuevo ? styles.avatarNuevo : ''}`}>
                              {d.esNuevo ? <UserPlus size={15} /> : getInitials(d.nombre)}
                            </div>
                            <div className={styles.pacienteInfo}>
                              <div className={styles.pacienteNombre}>
                                {d.nombre}
                                {d.esNuevo && <span className={styles.badgeNuevo}>Nuevo</span>}
                              </div>
                              <div className={styles.pacienteMeta}>
                                {d.cedula && <span>{d.cedula}</span>}
                                {d.cedula && consulta.pacienteEdad != null && <span aria-hidden="true">·</span>}
                                {consulta.pacienteEdad != null && <span>{consulta.pacienteEdad} años</span>}
                              </div>
                            </div>
                          </div>
                        </td>

                        {mostrarColumnaSala && (
                          <td><span className={styles.etiquetaSala}>{d.sala}</span></td>
                        )}

                        <td>
                          {d.motivo ? (
                            <button
                              type="button"
                              className={styles.motivo}
                              title="Ver el motivo completo"
                              onClick={() => setMotivoModal({ nombre: d.nombre, motivo: d.motivo })}
                            >
                              {d.motivo}
                            </button>
                          ) : (
                            <em className={styles.motivoVacio}>Sin especificar</em>
                          )}
                        </td>

                        <td>
                          <span className={`${styles.espera} ${styles[`espera_${d.nivel}`]}`}>
                            {formatWaitTime(consulta.creadoEn)}
                          </span>
                        </td>

                        <td className={styles.tdAccion}>{botonTomar(consulta, false)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* ── Tarjetas (pantallas angostas) ── */
            <div className={styles.listaTarjetas}>
              {sortedConsultas.map((consulta, index) => {
                const d = datosFila(consulta);
                const clases = [
                  styles.tarjeta,
                  index === 0 ? styles.tarjetaPrimera : '',
                  d.esNuevo ? styles.tarjetaNueva : '',
                  d.saliendo ? styles.filaSaliendo : styles.filaEntrando,
                ].filter(Boolean).join(' ');

                return (
                  <article key={consulta.id} className={clases}>
                    <div className={styles.tarjetaCabecera}>
                      {puedeCancelar && (
                        <input
                          type="checkbox"
                          className={styles.check}
                          checked={selectedIds.has(consulta.id)}
                          onChange={() => toggleSelected(consulta.id)}
                          aria-label={`Seleccionar a ${d.nombre}`}
                        />
                      )}
                      <div className={`${styles.avatar} ${d.esNuevo ? styles.avatarNuevo : ''}`}>
                        {d.esNuevo ? <UserPlus size={15} /> : getInitials(d.nombre)}
                      </div>
                      <div className={styles.pacienteInfo}>
                        <div className={styles.pacienteNombre}>
                          {d.nombre}
                          {d.esNuevo && <span className={styles.badgeNuevo}>Nuevo</span>}
                        </div>
                        <div className={styles.pacienteMeta}>
                          {d.cedula && <span>{d.cedula}</span>}
                          {d.cedula && consulta.pacienteEdad != null && <span aria-hidden="true">·</span>}
                          {consulta.pacienteEdad != null && <span>{consulta.pacienteEdad} años</span>}
                        </div>
                      </div>
                      <span className={`${styles.espera} ${styles[`espera_${d.nivel}`]}`}>
                        {formatWaitTime(consulta.creadoEn)}
                      </span>
                    </div>

                    {mostrarColumnaSala && (
                      <span className={styles.etiquetaSala}>{d.sala}</span>
                    )}

                    {d.motivo ? (
                      <button
                        type="button"
                        className={styles.tarjetaMotivo}
                        onClick={() => setMotivoModal({ nombre: d.nombre, motivo: d.motivo })}
                      >
                        {d.motivo}
                      </button>
                    ) : (
                      <em className={styles.motivoVacio}>Sin motivo especificado</em>
                    )}

                    {botonTomar(consulta, true)}
                  </article>
                );
              })}
            </div>
          )
        )}

        {/* ── Ofertas híbridas sin responder — solo supervisor ── */}
        {esSupervisor && tabActiva === 'ofertas' && (
          ofertasPendientes.length === 0 ? (
            <EmptyState
              variant="queue"
              title="No hay ofertas pendientes"
              message="Cuando el sistema le ofrezca un paciente a un médico (modo híbrido) y aún no haya aceptado ni rechazado, aparecerá aquí."
            />
          ) : (
            <div className={styles.tarjetaTabla}>
              <table className={styles.tabla}>
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Sala</th>
                    <th>Médico asignado</th>
                    <th>Esperando respuesta</th>
                    <th className={styles.thAccion}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {ofertasPendientes.map((oferta) => (
                    <tr key={oferta.id}>
                      <td>{fixMojibake(oferta.pacienteNombre)}</td>
                      <td><span className={styles.etiquetaSala}>{oferta.salaNombre || salaMap[oferta.salaId]?.nombre || oferta.salaId}</span></td>
                      <td>{oferta.medicoNombre || 'Sin nombre'}</td>
                      <td>
                        <span className={`${styles.espera} ${styles[`espera_${nivelEspera(oferta.asignadaEnDate)}`]}`}>
                          {formatWaitTime(oferta.asignadaEnDate)}
                        </span>
                      </td>
                      <td className={styles.tdAccion}>
                        <button
                          type="button"
                          className={styles.btnDevolver}
                          disabled={devolviendoId === oferta.id}
                          onClick={() => handleDevolverACola(oferta)}
                        >
                          <DoorClosed size={15} aria-hidden="true" />
                          {devolviendoId === oferta.id ? 'Devolviendo…' : 'Devolver a la cola'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* ── Motivo completo ──────────────────────────────── */}
      <Modal
        open={motivoModal !== null}
        onClose={() => setMotivoModal(null)}
        title="Motivo de consulta"
        size="sm"
      >
        {motivoModal && (
          <div className={styles.motivoDialog}>
            <p className={styles.motivoDialogPatient}>{motivoModal.nombre}</p>
            <p className={styles.motivoDialogText}>{motivoModal.motivo}</p>
            <div className={styles.motivoDialogFoot}>
              <Button variant="secondary" size="sm" onClick={() => setMotivoModal(null)}>
                <X size={14} />
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: motivo de cancelación masiva */}
      <Modal
        open={showCancelModal}
        onClose={() => {
          if (cancelando) return;
          setShowCancelModal(false);
        }}
        title="Cancelar consultas en espera"
        size="sm"
      >
        <div className={styles.cancelModalBody}>
          <p className={styles.cancelModalIntro}>
            Se cancelará{selectedIds.size === 1 ? '' : 'n'} <strong>{selectedIds.size}</strong>{' '}
            paciente{selectedIds.size === 1 ? '' : 's'} en espera y se les notificará el motivo
            (push y, si están en la pantalla de espera de la app, también en tiempo real).
          </p>
          <label className={styles.cancelModalLabel} htmlFor="motivo-cancelacion">
            Motivo de la cancelación
          </label>
          <textarea
            id="motivo-cancelacion"
            className={styles.cancelModalTextarea}
            value={motivoCancelacion}
            onChange={(e) => setMotivoCancelacion(e.target.value)}
            placeholder="Ej: No hay médicos disponibles en este momento, por favor solicite una nueva teleconsulta más tarde."
            rows={4}
            maxLength={500}
            autoFocus
            disabled={cancelando}
          />
          <div className={styles.cancelModalActions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCancelModal(false)}
              disabled={cancelando}
            >
              Volver
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={cancelando}
              disabled={!motivoCancelacion.trim()}
              onClick={handleConfirmCancelacion}
            >
              Confirmar cancelación
            </Button>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
}
